import { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Cap } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { quoteCardGroups } from './quoteCardEntries'
import { CARD_PARAM, QuoteCardEntryPicker } from './QuoteCardEntryPicker'
import { QuoteCardSideNav } from './QuoteCardSideNav'
import { useLiveCarrierDetails } from './useLiveCarrierDetails'

/**
 * Prototype for a new quotes screen — mobile-friendly QuoteCards shown in a
 * phone-width column, the primary layout this screen needs to work well in.
 */
const BRIEF = (
  <div className="border-ink-700 grid grid-cols-4 gap-6 border-t pt-6">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Create a standalone &quot;Quotes Page&quot; with a stable URL for brokers and our customers to use as
        an always up-to-date reflection of their offers, including indications and quotes. This QuoteCard
        component would replace our ChatIndicationCard.
      </p>
    </div>

    <div>
      <Cap onDark>Project: Sub-5 self-serve</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Enable brokers to focus their attention and expertise on quotes (not collecting information and
        filling applications), and helping bigger customers with more complex insurance decisions.
      </p>
    </div>

    <div>
      <Cap onDark>Feedback</Cap>
      <ul className="text-ink-200 mt-2 flex flex-col gap-1 text-xs leading-relaxed">
        <li>
          Slack channel:{' '}
          <a
            href="https://cailabsinc.slack.com/archives/C0BLBNGCZPF"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            #test-quote-page-v1
          </a>
        </li>
        <li>
          <a
            href="https://www.figma.com/design/s5rPNMOjPfBAIbivX1nrpR/Quotes-Review?node-id=0-1&p=f&t=cTQVH75vhxJVY3KS-0"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            Review Figma (screenshots)
          </a>
        </li>
      </ul>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Design surrounding quote page, with summary of application at the top and a checkout page interface
        for feedback.
      </p>
    </div>
  </div>
)

export function QuoteCardPrototypePage() {
  const live = useLiveCarrierDetails()
  const groups = useMemo(() => quoteCardGroups(live), [live])
  const [searchParams, setSearchParams] = useSearchParams()

  const entries = groups.flatMap((group) => group.entries)
  const selected = entries.find((entry) => entry.id === searchParams.get(CARD_PARAM)) ?? entries[0]

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [selected.id])

  return (
    <PrototypeFrame
      title="Quote card"
      owner="Carrie Phillips"
      date="July 28, 2026"
      status="Prototype, 0 rounds of feedback"
      brief={BRIEF}
      fullBleedContent
    >
      <Helmet>
        <title>Prototype: Quote card</title>
      </Helmet>
      <div className="flex">
        {/* Sticks under the prototype bar, which is 2.5rem tall. */}
        <aside className="bg-surface border-ink-200 no-scrollbar sticky top-10 hidden h-[calc(100svh-2.5rem)] w-64 shrink-0 flex-col overflow-x-hidden overflow-y-auto overscroll-contain border-r text-neutral-600 lg:flex">
          <QuoteCardSideNav groups={groups} selectedId={selected.id} />
        </aside>

        <div className="min-w-0 flex-1 px-4 py-10 sm:px-6">
          <div className="mx-auto flex w-full max-w-sm flex-col gap-8 lg:max-w-3xl">
            <header className="flex flex-col gap-4">
              <h1 className="text-ink text-3xl font-extrabold tracking-tight">Quote card</h1>
              <div className="lg:hidden">
                <QuoteCardEntryPicker
                  groups={groups}
                  selectedId={selected.id}
                  onSelect={(id) => setSearchParams({ [CARD_PARAM]: id })}
                />
              </div>
            </header>
            {/* Keyed so a demo's own state starts over when another state is picked. */}
            <div key={selected.id} className="flex flex-col gap-6">
              {selected.description}
              {selected.content}
            </div>
          </div>
        </div>
      </div>
    </PrototypeFrame>
  )
}
